import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Card, Btn, Pill, Notice, inputCls } from "@/dashboard/ui";
import { Mic, MicOff, Square, Send, Bot, User as UserIcon, AlertTriangle, Radio, Hand } from "lucide-react";

const LANG_MAP = {
  "English (UK)": "en-GB", "English (US)": "en-US",
  Spanish: "es-ES", French: "fr-FR", Hindi: "hi-IN",
  "Hindi (हिन्दी)": "hi-IN", "Telugu (తెలుగు)": "te-IN",
};

const detectSpokenLang = (text, fallback) => {
  if (/[\u0C00-\u0C7F]/.test(text)) return "te-IN";
  if (/[\u0900-\u097F]/.test(text)) return "hi-IN";
  return fallback;
};

const STATUS_LABELS = {
  idle: "Ready",
  connecting: "Connecting…",
  listening: "Listening — just speak",
  recording: "Listening… tap the mic to send",
  processing: "Processing…",
  speaking: "AI is speaking…",
  muted: "Microphone muted",
  error: "Needs attention",
};

// Voice-activity tuning (hands-free mode)
const SPEECH_END_MS = 1100;      // silence after speech ends a turn (dictated numbers pause mid-way)
const MIN_SEGMENT_MS = 600;      // shorter segments are discarded as noise
const MAX_SEGMENT_MS = 25000;    // force-turn cap
const RESUME_DELAY_MS = 350;     // gap after AI audio before listening resumes
const DEDUPE_MS = 6000;          // identical transcript resubmission window

function LiveWaveform({ levelRef, active }) {
  const barsRef = useRef(null);
  useEffect(() => {
    let raf;
    const tick = () => {
      const level = active ? Math.min(1, (levelRef.current || 0) * 6) : 0;
      const bars = barsRef.current?.children || [];
      for (let i = 0; i < bars.length; i++) {
        const jitter = 0.35 + 0.65 * Math.abs(Math.sin(i * 1.7 + performance.now() / 180));
        bars[i].style.height = `${Math.max(8, level * jitter * 100)}%`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, levelRef]);
  return (
    <div ref={barsRef} data-testid="live-waveform" role="img" aria-label="Live microphone level"
      className="flex h-16 w-full items-center justify-center gap-[3px] overflow-hidden sm:h-20">
      {Array.from({ length: 40 }).map((_, i) => (
        <span key={i} className={`w-1 rounded-full sm:w-1.5 ${i % 3 === 0 ? "bg-[#111111]" : i % 3 === 1 ? "bg-neutral-400" : "bg-neutral-300"}`} style={{ height: "8%" }} />
      ))}
    </div>
  );
}

const DEMO_LANGS = [
  ["en-GB", "English (UK)"], ["en-US", "English (US)"],
  ["te-IN", "Telugu (తెలుగు)"], ["hi-IN", "Hindi (हिन्दी)"],
];

function demoErrorMessage(e) {
  const s = e?.response?.status;
  if (s === 401 || s === 403) return "Please sign in again — the demo needs an authenticated account with AI access.";
  if (s === 404) return "That demo session wasn't found (it may have expired after an hour idle). Start a new session.";
  if (s === 409) return e?.response?.data?.detail || "This demo session has ended or hit its turn limit — start a new session.";
  if (s === 422) return e?.response?.data?.detail || "That message couldn't be used (empty or over 500 characters).";
  if (s === 429) return "Too many turns — please wait a moment before sending more.";
  if (s === 503) return "The demo service is temporarily unavailable. Please try again shortly.";
  return "Network problem — couldn't reach the demo service. Check your connection and try again.";
}

// Text-only offline demonstration of the cascaded voice pipeline (SK-M15B-4-03).
// No microphone, no audio, no vendors — messages go to POST /voice/pipeline-demo.
function PipelineDemo() {
  const [lang, setLang] = useState("en-GB");
  const [sessionId, setSessionId] = useState(null);
  const [turns, setTurns] = useState([]);   // {role, text, actions?}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sessionStatus, setSessionStatus] = useState("active");
  const [activeLang, setActiveLang] = useState("en-GB");
  const bottomRef = useRef(null);

  // --- Hear replies (Beta): opt-in browser speechSynthesis playback ---
  const ttsOk = typeof window !== "undefined" && "speechSynthesis" in window;
  const [hearOn, setHearOn] = useState(false);          // default OFF
  const [speaking, setSpeaking] = useState(false);
  const [voiceNotice, setVoiceNotice] = useState("");

  useEffect(() => () => { try { window.speechSynthesis?.cancel(); } catch { /* noop */ } }, []);

  const stopSpeaking = () => {
    try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
    if (mountedRef.current) setSpeaking(false);
  };

  const speakReply = (text) => {
    if (!hearOn || !ttsOk || !text) return;
    stopSpeaking();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = activeLang;
    const voices = window.speechSynthesis.getVoices();
    const exact = voices.filter((v) => v.lang === activeLang);
    const family = voices.filter((v) => v.lang && v.lang.startsWith(activeLang.split("-")[0]));
    const pool = exact.length ? exact : family;
    if (!pool.length) {
      // never silently substitute English for Telugu/Hindi (or any session language)
      const label = DEMO_LANGS.find(([v]) => v === activeLang)?.[1] || activeLang;
      setVoiceNotice(`No ${label} voice is available on this browser/device — the reply is shown as text only.`);
      return;
    }
    setVoiceNotice("");
    const best = pool.find((v) => /google|natural|neural|microsoft|premium|enhanced/i.test(v.name)) || pool[0];
    u.voice = best;
    u.onend = () => mountedRef.current && setSpeaking(false);
    u.onerror = () => mountedRef.current && setSpeaking(false);  // Safari may refuse; text stays visible
    window.speechSynthesis.speak(u);
    setSpeaking(true);
  };

  // --- Voice Input (Beta): browser-native SpeechRecognition, push-to-talk ---
  const srSupported = typeof window !== "undefined" &&
    !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  const [listening, setListening] = useState(false);
  const [micError, setMicError] = useState("");
  const [interim, setInterim] = useState("");   // live "hearing…" text — display only, never sent
  const recRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; stopMic(false); };  // discard + release on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commitText = (txt) => {
    // append-only: never overwrites text the user has typed or edited
    if (!mountedRef.current || !txt.trim()) return;
    setInput((prev) => ((prev ? prev.replace(/\s+$/, "") + " " : "") + txt.trim()).slice(0, 500));
  };

  const stopMic = (commitInterim = false) => {
    const rec = recRef.current;
    if (!rec) { setListening(false); setInterim(""); return; }
    // stop() lets pending final results flush before onend; onend decides whether the
    // last interim becomes an editable draft (only on an explicit user Stop).
    rec._commitInterim = commitInterim;
    rec._stopped = true;
    try { rec.stop(); } catch { /* not started */ }
    // safety net: if neither onend nor onerror fires (browser quirk), clean up anyway
    setTimeout(() => {
      if (recRef.current === rec) {
        if (rec._commitInterim && !rec._gotFinal && rec._lastInterim) commitText(rec._lastInterim);
        recRef.current = null;
        if (mountedRef.current) { setListening(false); setInterim(""); }
      }
    }, 1500);
  };

  const toggleMic = () => {
    if (listening) { stopMic(true); return; }        // explicit Stop → keep the transcript as a draft
    if (!srSupported || sending || sessionStatus === "ended") return;
    setMicError("");
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = sessionId ? activeLang : lang;        // session language sticks; pre-session follows the selector
    rec.interimResults = true;
    rec.continuous = false;
    rec._gotFinal = false;
    rec._lastInterim = "";
    rec._commitInterim = false;
    rec._stopped = false;
    const mine = rec;
    recRef.current = mine;
    rec.onresult = (ev) => {
      if (recRef.current !== mine || !mountedRef.current) return;  // stale callback guard
      let final = "", live = "";
      for (const r of ev.results) (r.isFinal ? final += r[0].transcript : live += r[0].transcript);
      // Only FINAL text is committed to the editable input; interim is display-only.
      if (final.trim()) { mine._gotFinal = true; commitText(final); }
      if (live.trim()) mine._lastInterim = live.trim();
      setInterim(live.trim());
    };
    rec.onend = () => {
      if (recRef.current !== mine) return;
      recRef.current = null;
      // Safari (and some engines) may end without ever delivering a final result —
      // keep the last interim as an editable draft, but ONLY on an explicit user Stop.
      if (mine._stopped && mine._commitInterim && !mine._gotFinal && mine._lastInterim) {
        commitText(mine._lastInterim);
      }
      if (mountedRef.current) { setListening(false); setInterim(""); }
    };
    rec.onerror = (ev) => {
      if (recRef.current !== mine) return;
      const msgs = {
        "not-allowed": "Microphone permission denied — typing still works.",
        "service-not-allowed": "Microphone permission denied — typing still works.",
        "language-not-supported": "This browser doesn't support speech recognition for the selected language — try Chrome or type your message.",
        "no-speech": "No speech detected — try again or type your message.",
        "audio-capture": "No microphone found — typing still works.",
        aborted: "",
      };
      let msg = msgs[ev.error] ?? "Speech recognition failed — typing still works.";
      // Some browsers (e.g. Safari) report unsupported locales as not-allowed/service-not-allowed.
      // Genuine permission failures still get the message above — this only ADDS cautious guidance
      // for the less-common languages, it doesn't reclassify the error.
      const recLang = mine.lang;
      if ((recLang === "te-IN" || recLang === "hi-IN") &&
          (ev.error === "not-allowed" || ev.error === "service-not-allowed" || ev.error === "language-not-supported")) {
        msg = "Your browser may not support speech recognition for this language. Try Chrome or type your message instead.";
      }
      setMicError(msg ? `${msg} (code: ${ev.error})` : "");
      if (mountedRef.current) { setListening(false); setInterim(""); }
      recRef.current = null;
    };
    try {
      rec.start();
      setListening(true);
    } catch {
      setMicError("Couldn't start speech recognition — typing still works.");
      recRef.current = null;
    }
  };

  // --- Recording fallback (Safari Telugu etc.): MediaRecorder → existing /voice/transcribe ---
  const recorderSupported = typeof MediaRecorder !== "undefined" &&
    typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const demoRecRef = useRef(null);

  useEffect(() => () => {                              // release recorder on unmount
    try { demoRecRef.current?.stream?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
  }, []);

  const toggleDemoRecording = async () => {
    if (recording) {
      try { demoRecRef.current?.rec?.stop(); } catch { /* noop */ }
      return;                                          // onstop continues the flow
    }
    if (!recorderSupported || sending || transcribing || sessionStatus === "ended") return;
    setMicError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]
        .find((m) => MediaRecorder.isTypeSupported(m)) || "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        demoRecRef.current = null;
        if (!mountedRef.current) return;
        setRecording(false);
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 100) { setMicError("Couldn't hear anything — try again or type."); return; }
        setTranscribing(true);
        try {
          const bt = (blob.type || "").toLowerCase();
          const ext = bt.includes("mp4") ? "mp4" : bt.includes("ogg") ? "ogg" : "webm";
          const fd = new FormData();
          fd.append("file", blob, `speech.${ext}`);
          fd.append("language", sessionId ? activeLang : lang);   // pin to the session language
          const { data } = await api.post("/voice/transcribe", fd);
          const text = (data.text || "").trim();
          if (text) commitText(text);                  // editable draft — never auto-sent
          else setMicError("Couldn't hear anything — try again or type.");
        } catch (e) {
          setMicError(e?.response?.data?.detail
            ? `Transcription failed (${e.response.data.detail}) — typing still works.`
            : "Transcription failed — typing still works.");
        } finally {
          if (mountedRef.current) setTranscribing(false);
        }
      };
      demoRecRef.current = { rec, stream };
      rec.start();
      setRecording(true);
    } catch {
      setMicError("Microphone unavailable or permission denied — typing still works.");
    }
  };

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [turns]);

  const fresh = () => {
    stopMic();                                       // release mic when starting a new session
    try { demoRecRef.current?.rec?.stop(); } catch { /* noop */ }
    stopSpeaking();
    setVoiceNotice("");
    setMicError("");
    setSessionId(null); setTurns([]); setInput("");
    setError(""); setSessionStatus("active");
  };

  const changeLang = (v) => {                        // language only changeable pre-session
    stopMic();
    stopSpeaking();
    setLang(v);
  };

  const send = async () => {
    const utterance = input.trim();
    if (!utterance || sending || sessionStatus === "ended") return;
    setSending(true);
    setError("");
    setInterim("");
    setTurns((t) => [...t, { role: "caller", text: utterance }]);
    setInput("");
    try {
      const { data } = await api.post("/voice/pipeline-demo", {
        utterance,
        ...(sessionId ? { session_id: sessionId } : { language: lang }),
      });
      setSessionId(data.session_id);
      setActiveLang(data.language);
      setSessionStatus(data.status);
      speakReply(data.reply);
      setTurns((t) => {
        const next = [...t];
        if (data.greeting) next.splice(next.length - 1, 0, { role: "agent", text: data.greeting });
        next.push({ role: "agent", text: data.reply, actions: data.simulated_actions || [] });
        return next;
      });
    } catch (e) {
      setTurns((t) => t.slice(0, -1)); // roll back the unsent utterance
      setInput(utterance);
      setError(demoErrorMessage(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="mx-auto flex max-w-2xl flex-col p-5" data-testid="pipeline-demo">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Pill tone="amber">Offline demo — text only</Pill>
        <Pill tone="outline">No audio · no CRM writes · no bookings</Pill>
        <span className="ml-auto flex items-center gap-2">
          <select className={inputCls + " w-auto py-1.5 text-xs"} value={lang} data-testid="demo-language"
            disabled={!!sessionId} onChange={(e) => changeLang(e.target.value)}>
            {DEMO_LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <Btn variant="ghost" className="px-3 py-1.5 text-xs" data-testid="demo-new-session" onClick={fresh}>
            New demo session
          </Btn>
        </span>
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5 text-xs text-neutral-600" data-testid="demo-hear-label">
          <input type="checkbox" checked={hearOn} data-testid="demo-hear-toggle" disabled={!ttsOk}
            onChange={(e) => { setHearOn(e.target.checked); if (!e.target.checked) stopSpeaking(); }}
            className="h-3.5 w-3.5 rounded" />
          Hear replies (Beta)
        </label>
        {speaking && (
          <button type="button" onClick={stopSpeaking} data-testid="demo-stop-speaking"
            className="flex items-center gap-1 rounded-full border border-neutral-300 px-2.5 py-1 text-[11px] font-semibold text-neutral-600 hover:border-neutral-400">
            <Square size={10} /> Stop speaking
          </button>
        )}
        {!ttsOk && <span className="text-[10px] text-neutral-400">Spoken replies aren't supported by this browser.</span>}
        {voiceNotice && <span className="text-[10px] text-amber-700" data-testid="demo-voice-notice">{voiceNotice}</span>}
      </div>
      <p className="mb-3 text-[11px] text-neutral-500">
        Chat with the offline receptionist pipeline using your Business Profile. Session status:{" "}
        <span className="font-semibold" data-testid="demo-status">{sessionStatus}</span> · Language:{" "}
        <span className="font-semibold" data-testid="demo-active-lang">{activeLang}</span>
        {sessionId && <span className="text-neutral-400"> · turns continue in this session until it ends or expires after 1h idle</span>}
      </p>

      <div className="min-h-[220px] flex-1 space-y-3 overflow-y-auto rounded-xl border border-neutral-200 bg-neutral-50 p-4" data-testid="demo-messages">
        {turns.length === 0 && (
          <p className="py-10 text-center text-xs text-neutral-400">
            Type a message below to start — e.g. “What services do you offer?” or “I'd like to book an appointment.”
          </p>
        )}
        {turns.map((m, i) => (
          <div key={i} className={`flex ${m.role === "caller" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-xs leading-relaxed ${
              m.role === "caller" ? "bg-[#111111] text-white" : "border border-neutral-200 bg-white text-neutral-800"}`}>
              <p>{m.text}</p>
              {m.actions?.map((a, j) => (
                <p key={j} className="mt-1.5 flex items-start gap-1.5 rounded-lg bg-amber-50 px-2 py-1 text-[10px] text-amber-800" data-testid={`demo-action-${i}-${j}`}>
                  <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                  <span><span className="font-bold">Simulated — no action performed.</span> {a.detail}</span>
                </p>
              ))}
            </div>
          </div>
        ))}
        {sending && <p className="text-center text-[11px] text-neutral-400" data-testid="demo-thinking">Thinking…</p>}
        <div ref={bottomRef} />
      </div>

      {error && (
        <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="demo-error">{error}</p>
      )}

      <div className="mt-3">
        <div className="mb-2 flex flex-wrap items-center gap-2" data-testid="demo-voice-input">
          <button
            type="button"
            data-testid="demo-mic"
            disabled={!srSupported || sending || sessionStatus === "ended"}
            onClick={toggleMic}
            aria-pressed={listening}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
              listening ? "bg-red-600 text-white" : "border border-neutral-300 text-neutral-600 hover:border-neutral-400"
            } ${(!srSupported || sessionStatus === "ended") ? "opacity-40" : ""}`}
          >
            {listening ? <Square size={12} /> : <Mic size={12} />}
            {listening ? "Listening — tap to stop" : "Voice Input (Beta)"}
          </button>
          {srSupported ? (
            <span className="text-[10px] text-neutral-400" data-testid="demo-mic-notice">
              Browser speech recognition may send your audio to your browser provider for processing.
            </span>
          ) : (
            <span className="text-[10px] text-neutral-400" data-testid="demo-mic-unsupported">
              Voice input isn't supported by this browser — typing works everywhere.
            </span>
          )}
          {micError && <span className="text-[10px] text-red-600" data-testid="demo-mic-error">{micError}</span>}
          {recorderSupported && (!srSupported || micError) && (
            <button type="button" data-testid="demo-record" onClick={toggleDemoRecording}
              disabled={sending || transcribing || sessionStatus === "ended"}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                recording ? "bg-red-600 text-white" : "border border-neutral-300 text-neutral-600 hover:border-neutral-400"
              }`}>
              {recording ? <><Square size={12} /> Recording — tap to stop</>
                : transcribing ? "Transcribing…"
                : <><Mic size={12} /> Record instead</>}
            </button>
          )}
        </div>
        <div className="flex gap-2">
        <input
          className={inputCls + " flex-1"}
          placeholder={sessionStatus === "ended" ? "Session ended — start a new demo session" : "Type a message (max 500 chars)…"}
          value={input} maxLength={500} data-testid="demo-input"
          disabled={sessionStatus === "ended"}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
        />
        <Btn data-testid="demo-send" disabled={sending || !input.trim() || sessionStatus === "ended"} onClick={send}>
          <Send size={14} className="mr-1.5" />{sending ? "Sending…" : "Send"}
        </Btn>
        </div>
        {interim && (
          <p className="mt-1.5 text-[11px] italic text-neutral-400" data-testid="demo-interim">Hearing: {interim}…</p>
        )}
      </div>
    </Card>
  );
}

export default function TalkToAI() {
  const [params] = useSearchParams();
  const [uiMode, setUiMode] = useState("voice");   // "voice" | "demo"
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState(params.get("agent") || "");
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [active, setActive] = useState(false);
  const [recording, setRecording] = useState(false); // push-to-talk mic state
  const [mode, setMode] = useState("handsfree");     // "handsfree" | "ptt"
  const [muted, setMuted] = useState(false);
  const [text, setText] = useState("");
  const recorderRef = useRef(null);   // push-to-talk recorder
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const activeRef = useRef(false);
  const sessionRef = useRef(null);
  const bottomRef = useRef(null);
  // hands-free engine refs
  const modeRef = useRef("handsfree");
  const mutedRef = useRef(false);
  const hfBusyRef = useRef(false);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const hfRecRef = useRef(null);
  const vadTimerRef = useRef(null);
  const pttLevelTimerRef = useRef(null);
  const segRef = useRef({ speech: false, lastSpeech: 0, start: 0, discard: false });
  const lastSentRef = useRef({ text: "", at: 0 });
  const sendingRef = useRef(false);
  const levelRef = useRef(0);
  const noiseFloorRef = useRef(0.02);
  const greetedRef = useRef(false);
  const [needsResume, setNeedsResume] = useState(false);
  const turnTimingRef = useRef(null);
  const [lastTiming, setLastTiming] = useState(null);

  const ttsSupported = typeof window !== "undefined" && "speechSynthesis" in window;
  const recorderSupported =
    typeof window !== "undefined" &&
    typeof MediaRecorder !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia;
  const agent = agents.find((a) => a.id === agentId);
  const lang = LANG_MAP[agent?.language] || "en-GB";

  useEffect(() => {
    api.get("/voice-agents").then((r) => setAgents(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);
  useEffect(() => () => teardownAudio(), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Background tabs throttle timers — finish any open segment so a stale
  // recording can't linger, and on return, resume honestly or ask the user.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "hidden") {
        if (modeRef.current === "handsfree" && hfRecRef.current?.state === "recording") finishSegment();
        return;
      }
      if (!activeRef.current || modeRef.current !== "handsfree" || mutedRef.current) return;
      const ctx = audioCtxRef.current;
      const tryResume = async () => {
        try { await ctx?.resume(); } catch {}
        if (ctx && ctx.state === "suspended") {
          setNeedsResume(true);
          setStatus("error");
          setErrorMsg("Audio was paused while the tab was away — press Resume Conversation to continue.");
        } else {
          setNeedsResume(false);
          setErrorMsg("");
          hfBusyRef.current = false;
          resumeListeningSoon(0);
        }
      };
      void tryResume();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // close() returns a Promise — try/catch misses its async rejection when a context
  // is closed twice (mode switch + teardown). Null the ref first, then guard state.
  function closeAudioCtx(ref) {
    const ctx = ref.current;
    ref.current = null;
    if (ctx && ctx.state !== "closed") ctx.close().catch(() => {});
  }

  function teardownAudio() {
    activeRef.current = false;
    if (vadTimerRef.current) clearInterval(vadTimerRef.current);
    if (pttLevelTimerRef.current) { clearInterval(pttLevelTimerRef.current); pttLevelTimerRef.current = null; }
    levelRef.current = 0;
    try { recorderRef.current?.stop(); } catch {}
    try { if (hfRecRef.current?.state === "recording") hfRecRef.current.stop(); } catch {}
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    closeAudioCtx(audioCtxRef);
    try { audioRef.current?.pause(); } catch {}
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
  }

  const ensureSession = async () => {
    if (sessionRef.current) return sessionRef.current;
    const { data } = await api.post(`/voice/session${agentId ? `?agent_id=${agentId}` : ""}`);
    sessionRef.current = data.id;
    return data.id;
  };

  const browserSpeak = (reply, onDone) => {
    if (!ttsSupported) { onDone?.(); return; }
    const spokenLang = detectSpokenLang(reply, lang);
    const u = new SpeechSynthesisUtterance(reply);
    u.lang = spokenLang;
    const deviceVoices = window.speechSynthesis.getVoices();
    const exact = deviceVoices.filter((v) => v.lang === spokenLang);
    const family = deviceVoices.filter((v) => v.lang && v.lang.startsWith(spokenLang.split("-")[0]));
    const pool = exact.length ? exact : family;
    const match = pool.find((v) => /google|natural|neural|microsoft|premium|enhanced/i.test(v.name)) || pool[0];
    if (match) u.voice = match;
    u.rate = Math.min(1.3, Math.max(0.7, Number(agent?.speaking_speed) || 1));
    u.onend = () => onDone?.();
    u.onerror = () => onDone?.();
    window.speechSynthesis.speak(u);
  };

  const speak = async (reply, onDone) => {
    if (!activeRef.current) { setStatus("idle"); return; } // conversation ended mid-turn
    const done = () => {
      // Status must reflect reality: beginSegment sets "listening" when the
      // recorder truly restarts; until then we stay honest about the state.
      onDone?.();
      if (!activeRef.current) setStatus("idle");
      else if (mutedRef.current) setStatus("muted");
      else if (modeRef.current === "ptt") setStatus("idle");
      // hands-free: "listening" is set by beginSegment() a moment later
    };
    if (!reply) { done(); return; }
    // never overlap playback: stop whatever is currently speaking first
    try { audioRef.current?.pause(); } catch {}
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    setStatus("speaking");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/voice/speak`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("sk_token")}`,
        },
        body: JSON.stringify({ text: reply, agent_id: agentId || null }),
      });
      if (!res.ok) throw new Error("speech engine unavailable");
      const blob = await res.blob();
      if (turnTimingRef.current) turnTimingRef.current.speech = ((performance.now() - turnTimingRef.current.t0) / 1000).toFixed(1);
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { URL.revokeObjectURL(url); done(); };
      audio.onerror = () => { URL.revokeObjectURL(url); browserSpeak(reply, done); };
      await audio.play();
      if (turnTimingRef.current) {
        turnTimingRef.current.playing = ((performance.now() - turnTimingRef.current.t0) / 1000).toFixed(1);
        setLastTiming({ ...turnTimingRef.current });
      }
    } catch {
      browserSpeak(reply, done);
    }
  };

  // ---- push-to-talk (unchanged behaviour) ----

  const startRecording = async () => {
    setErrorMsg("");
    if (!recorderSupported) {
      setStatus("error");
      setErrorMsg("Microphone recording is not supported in this browser — type your message below instead.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      // live level metering so the waveform shows real captured audio
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx();
        const src = ctx.createMediaStreamSource(stream);
        const an = ctx.createAnalyser();
        an.fftSize = 512;
        src.connect(an);
        pttLevelTimerRef.current = setInterval(() => {
          const buf = new Uint8Array(an.fftSize);
          an.getByteTimeDomainData(buf);
          let s = 0;
          for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; s += v * v; }
          levelRef.current = Math.sqrt(s / buf.length);
        }, 100);
        rec._pttCtx = ctx;
      } catch {}
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        if (pttLevelTimerRef.current) { clearInterval(pttLevelTimerRef.current); pttLevelTimerRef.current = null; }
        levelRef.current = 0;
        try { rec._pttCtx?.close().catch(() => {}); rec._pttCtx = null; } catch {}
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        if (!activeRef.current) return;
        setStatus("processing");
        turnTimingRef.current = { t0: performance.now() };
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        await transcribeAndSend(blob);
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
      setStatus("recording");
    } catch {
      setStatus("error");
      setErrorMsg("Microphone access was denied. Allow it in your browser settings, or type below.");
    }
  };

  const stopRecording = () => {
    try { recorderRef.current?.stop(); } catch { setRecording(false); setStatus("idle"); }
  };

  const transcribeAndSend = async (blob) => {
    try {
      const fd = new FormData();
      // Safari records audio/mp4 — name the file after the REAL container or Whisper fails
      const bt = (blob.type || "").toLowerCase();
      const ext = bt.includes("mp4") ? "mp4" : bt.includes("ogg") ? "ogg" : bt.includes("mpeg") ? "mp3" : "webm";
      fd.append("file", blob, `speech.${ext}`);
      if (agentId) fd.append("agent_id", agentId); // pins transcription to the agent's language
      const { data } = await api.post("/voice/transcribe", fd);
      if (turnTimingRef.current) turnTimingRef.current.transcribe = ((performance.now() - turnTimingRef.current.t0) / 1000).toFixed(1);
      const transcript = (data.text || "").trim();
      if (!transcript) {
        setMessages((m) => [...m, { role: "system", text: "I couldn't hear anything — try speaking closer to the mic, or type below." }]);
        setStatus("idle");
        hfBusyRef.current = false;
        resumeListeningSoon(0);
        return;
      }
      // discard duplicate submissions of the same transcript within a few seconds
      const norm = transcript.toLowerCase();
      const now = Date.now();
      if (modeRef.current === "handsfree" && norm === lastSentRef.current.text && now - lastSentRef.current.at < DEDUPE_MS) {
        hfBusyRef.current = false;
        resumeListeningSoon(0);
        return;
      }
      lastSentRef.current = { text: norm, at: now };
      await sendMessage(transcript);
    } catch {
      hfBusyRef.current = false;
      if (modeRef.current === "handsfree" && activeRef.current) {
        setErrorMsg("Transcription failed — still listening, please try again.");
        resumeListeningSoon(0);
      } else {
        setStatus("error");
        setErrorMsg("Transcription failed — the speech engine may be unreachable. You can still type below.");
      }
    }
  };

  const sendMessage = async (raw) => {
    const msg = (raw || "").trim();
    if (!msg || sendingRef.current) return;
    sendingRef.current = true;
    setErrorMsg("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setStatus("processing");
    try {
      const sid = await ensureSession();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/voice/respond`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("sk_token")}`,
        },
        body: JSON.stringify({ message: msg, session_id: sid, agent_id: agentId || null }),
      });
      const ct = res.headers.get("content-type") || "";
      if (ct.includes("text/event-stream")) {
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let reply = "", buffer = "", failed = false;
        setMessages((m) => [...m, { role: "agent", text: "" }]);
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          buffer = parts.pop();
          for (const p of parts) {
            if (!p.startsWith("data: ")) continue;
            try {
              const payload = JSON.parse(p.slice(6));
              if (payload.token) {
                reply += payload.token;
                const snapshot = reply;
                setMessages((m) => {
                  const copy = [...m];
                  copy[copy.length - 1] = { role: "agent", text: snapshot };
                  return copy;
                });
              }
              if (payload.error) { failed = true; setErrorMsg(payload.error); }
            } catch {}
          }
        }
        if (failed || !reply) {
          setMessages((m) => [...m.slice(0, -1), { role: "system", text: "The AI model could not respond. Please try again." }]);
          hfBusyRef.current = false;
          setStatus("idle");
          resumeListeningSoon(0);
        } else if (!activeRef.current) {
          setStatus("idle"); // ended while the reply was in flight
        } else {
          if (turnTimingRef.current) {
            turnTimingRef.current.ai = ((performance.now() - turnTimingRef.current.t0) / 1000).toFixed(1);
            setLastTiming({ ...turnTimingRef.current }); // visible even if audio playback is blocked
          }
          speak(reply, () => {
            hfBusyRef.current = false; // turn complete — listening may resume
            resumeListeningSoon(RESUME_DELAY_MS);
          });
        }
      } else {
        const data = await res.json();
        if (data.connected === false) {
          setMessages((m) => [...m, { role: "system", text: "AI model not connected — your message was captured, but no AI reply is available until a model is connected." }]);
          setStatus("idle");
          resumeListeningSoon(0);
        } else if (data.reply) {
          setMessages((m) => [...m, { role: "agent", text: data.reply }]);
          speak(data.reply, () => {
            hfBusyRef.current = false;
            resumeListeningSoon(RESUME_DELAY_MS);
          });
        }
      }
    } catch {
      hfBusyRef.current = false;
      // hands-free: beginSegment() will set "listening" truthfully; don't claim it here
      if (!(modeRef.current === "handsfree" && activeRef.current)) setStatus("error");
      setErrorMsg("Could not reach the AI service. Check your connection and try again.");
      resumeListeningSoon(0);
    } finally {
      sendingRef.current = false;
    }
  };

  // ---- hands-free engine ----

  function resumeListeningSoon(delay) {
    if (!activeRef.current || modeRef.current !== "handsfree" || mutedRef.current || hfBusyRef.current) return;
    setTimeout(() => {
      if (!activeRef.current || modeRef.current !== "handsfree" || mutedRef.current || hfBusyRef.current) return;
      if (hfRecRef.current?.state === "recording") return; // already listening
      beginSegment();
    }, delay);
  }

  function beginSegment() {
    const stream = streamRef.current;
    if (!stream || hfRecRef.current?.state === "recording") return;
    const rec = new MediaRecorder(stream);
    hfRecRef.current = rec;
    chunksRef.current = [];
    segRef.current = { speech: false, lastSpeech: 0, start: Date.now(), discard: false };
    rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
    rec.onstop = () => { void handleSegment(rec); };
    rec.start();
    setStatus("listening");
    if (vadTimerRef.current) clearInterval(vadTimerRef.current);
    vadTimerRef.current = setInterval(checkVad, 100);
  }

  function checkVad() {
    const analyser = analyserRef.current;
    const rec = hfRecRef.current;
    if (!analyser || !rec || rec.state !== "recording") return;
    const buf = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = (buf[i] - 128) / 128;
      sum += v * v;
    }
    const rms = Math.sqrt(sum / buf.length);
    levelRef.current = rms; // drives the real level waveform
    const now = Date.now();
    const seg = segRef.current;
    // adaptive noise floor: ambient level tracked while nobody is speaking,
    // threshold floats above it so background noise never triggers a turn
    if (!seg.speech) {
      noiseFloorRef.current = noiseFloorRef.current * 0.95 + rms * 0.05;
    }
    const threshold = Math.min(0.08, Math.max(0.015, noiseFloorRef.current * 3));
    if (rms > threshold) {
      seg.speech = true;
      seg.lastSpeech = now;
    } else if (seg.speech && now - seg.lastSpeech > SPEECH_END_MS) {
      finishSegment();
    } else if (now - seg.start > MAX_SEGMENT_MS) {
      finishSegment();
    }
  }

  function finishSegment() {
    if (vadTimerRef.current) { clearInterval(vadTimerRef.current); vadTimerRef.current = null; }
    const rec = hfRecRef.current;
    hfBusyRef.current = true; // processing — don't listen to ourselves / submit twice
    try { rec?.stop(); } catch { hfBusyRef.current = false; }
  }

  async function handleSegment(rec) {
    const seg = segRef.current;
    const duration = Date.now() - seg.start;
    const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
    chunksRef.current = [];
    if (seg.discard || !seg.speech || duration < MIN_SEGMENT_MS) {
      // noise-only / too short / muted — keep listening silently
      hfBusyRef.current = false;
      resumeListeningSoon(0);
      return;
    }
    turnTimingRef.current = { t0: performance.now() };
    setStatus("processing");
    await transcribeAndSend(blob);
  }

  const startHandsFree = async () => {
    setErrorMsg("");
    setStatus("connecting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      hfBusyRef.current = false;
      beginSegment();
    } catch {
      setStatus("error");
      setErrorMsg("Microphone access was denied. Allow it in your browser settings, or switch to push-to-talk / typing below.");
    }
  };

  const toggleMute = () => {
    const next = !mutedRef.current;
    mutedRef.current = next;
    setMuted(next);
    try { streamRef.current?.getTracks().forEach((t) => { t.enabled = !next; }); } catch {}
    if (next) {
      segRef.current.discard = true;
      try { if (hfRecRef.current?.state === "recording") hfRecRef.current.stop(); } catch {}
      if (vadTimerRef.current) { clearInterval(vadTimerRef.current); vadTimerRef.current = null; }
      hfBusyRef.current = false;
      setStatus("muted");
    } else {
      resumeListeningSoon(0);
    }
  };

  const switchMode = async (next) => {
    if (next === modeRef.current) return;
    // cleanly stop whatever the current mode is doing
    if (vadTimerRef.current) { clearInterval(vadTimerRef.current); vadTimerRef.current = null; }
    segRef.current.discard = true;
    try { if (hfRecRef.current?.state === "recording") hfRecRef.current.stop(); } catch {}
    try { recorderRef.current?.stop(); } catch {}
    setRecording(false);
    hfBusyRef.current = false;
    hfRecRef.current = null;
    mutedRef.current = false;
    setMuted(false);
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    closeAudioCtx(audioCtxRef);
    streamRef.current = null;
    modeRef.current = next;
    setMode(next);
    levelRef.current = 0;
    setNeedsResume(false);
    if (next === "handsfree") await startHandsFree();
    else setStatus("idle");
  };

  const start = async () => {
    setErrorMsg("");
    setStatus("connecting");
    try { await ensureSession(); } catch {}
    activeRef.current = true;
    setActive(true);
    const beginMic = async () => {
      if (modeRef.current === "handsfree") {
        if (recorderSupported) await startHandsFree();
        else {
          modeRef.current = "ptt";
          setMode("ptt");
          setStatus("idle");
          setErrorMsg("Hands-free recording is not supported in this browser — push-to-talk is ready instead.");
        }
      } else {
        setStatus("idle");
      }
    };
    if (agent?.greeting && messages.length === 0 && !greetedRef.current) {
      greetedRef.current = true;
      setMessages([{ role: "agent", text: agent.greeting }]);
      speak(agent.greeting, () => { void beginMic(); });
    } else {
      await beginMic();
    }
  };

  const stop = () => {
    teardownAudio();
    hfBusyRef.current = false;
    hfRecRef.current = null;
    streamRef.current = null;
    mutedRef.current = false;
    setMuted(false);
    setNeedsResume(false);
    levelRef.current = 0;
    setActive(false);
    setRecording(false);
    setStatus("idle");
  };

  const handsfreeActive = active && mode === "handsfree";

  return (
    <div data-testid="talk-page">
      <PageHeader
        title="Talk to AI"
        sub="Start a hands-free conversation and just speak — the mic listens for your turns, and the AI answers aloud. Push-to-talk and typing remain available as fallbacks. This is a demonstration, not a phone call: telephony arrives with LiveKit Agents / Pipecat."
      />

      <div className="mb-4 flex gap-1.5" data-testid="talk-uimode">
        <button data-testid="uimode-voice" onClick={() => setUiMode("voice")}
          className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${uiMode === "voice" ? "bg-[#111111] text-white" : "border border-neutral-300 text-neutral-600 hover:border-neutral-400"}`}>
          Talk to AI
        </button>
        <button data-testid="uimode-demo" onClick={() => setUiMode("demo")}
          className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${uiMode === "demo" ? "bg-[#111111] text-white" : "border border-neutral-300 text-neutral-600 hover:border-neutral-400"}`}>
          Pipeline Demo (Offline)
        </button>
      </div>

      {uiMode === "demo" ? <PipelineDemo /> : (
      <div className="grid gap-4 lg:grid-cols-[1fr_1.1fr]">
        <Card className="flex min-w-0 flex-col items-center p-8 text-center" data-testid="talk-console">
          <div className="mb-5 flex flex-wrap items-center justify-center gap-2">
            <Pill tone="outline">Voice demonstration — not a phone call</Pill>
            {active && (
              <Pill tone="outline">
                <span data-testid="talk-mode">{mode === "handsfree" ? "Hands-free" : "Push-to-talk"}</span>
              </Pill>
            )}
            <Pill tone={status === "error" ? "red" : status === "idle" ? "grey" : "black"}>
              <span data-testid="talk-status">{STATUS_LABELS[status]}</span>
            </Pill>
          </div>

          <div className={`w-full rounded-2xl border p-6 transition-colors ${
            status === "recording" || status === "speaking" || status === "listening" ? "border-[#111111] bg-neutral-50" : "border-neutral-200 bg-white"
          }`}>
            <LiveWaveform levelRef={levelRef} active={status === "listening" || status === "recording"} />
            <p className="mt-1 text-[10px] uppercase tracking-wider text-neutral-400">
              {status === "listening" || status === "recording" ? "Live microphone level" : status === "speaking" ? "AI speaking — mic paused" : "Microphone idle"}
            </p>
          </div>

          <div className="mt-6 flex flex-col items-center gap-4">
            {!active ? (
              <>
                <Btn data-testid="talk-start-button" onClick={start} className="px-7 py-3">
                  <Mic size={16} /> Start Conversation
                </Btn>
                <p className="max-w-xs text-xs leading-relaxed text-neutral-400">
                  Hands-free: speak naturally and the AI answers aloud. Your mic only opens after you press start.
                </p>
              </>
            ) : (
              <>
                {mode === "handsfree" ? (
                  <button
                    data-testid="talk-mute-button"
                    onClick={toggleMute}
                    aria-label={muted ? "Unmute microphone" : "Mute microphone"}
                    className={`flex h-20 w-20 items-center justify-center rounded-full transition-all ${
                      muted
                        ? "bg-neutral-300 text-neutral-500"
                        : status === "listening"
                          ? "animate-pulse bg-[#111111] text-white ring-8 ring-neutral-200"
                          : "bg-[#111111] text-white opacity-60"
                    }`}
                  >
                    {muted ? <MicOff size={26} /> : <Mic size={26} />}
                  </button>
                ) : (
                  <button
                    data-testid="talk-mic-button"
                    onClick={recording ? stopRecording : startRecording}
                    disabled={status === "processing" || status === "speaking"}
                    aria-label={recording ? "Stop and send" : "Speak"}
                    className={`flex h-20 w-20 items-center justify-center rounded-full bg-[#111111] text-white transition-all hover:bg-neutral-800 disabled:opacity-40 ${
                      recording ? "animate-pulse ring-8 ring-neutral-200" : ""
                    }`}
                  >
                    <Mic size={26} />
                  </button>
                )}
                <p className="text-xs text-neutral-400" data-testid="talk-hint">
                  {mode === "handsfree"
                    ? muted ? "Muted — tap to unmute" : status === "listening" ? "Listening… just speak" : status === "speaking" ? "AI is speaking…" : status === "processing" ? "Thinking…" : "…"
                    : recording ? "Tap again to send" : "Tap the mic and speak"}
                </p>
                {lastTiming?.ai && (
                  <p className="text-[10px] text-neutral-400" data-testid="turn-timing">
                    Last turn: transcription {lastTiming.transcribe}s · AI {lastTiming.ai}s · speech {lastTiming.speech}s · playing after {lastTiming.playing}s
                  </p>
                )}
                <div className="flex flex-wrap items-center justify-center gap-2">
                  {needsResume && (
                    <Btn data-testid="talk-resume-button" onClick={async () => {
                      try { await audioCtxRef.current?.resume(); } catch {}
                      setNeedsResume(false);
                      setErrorMsg("");
                      hfBusyRef.current = false;
                      resumeListeningSoon(0);
                    }}>
                      <Mic size={14} /> Resume Conversation
                    </Btn>
                  )}
                  <Btn data-testid="talk-mode-toggle" onClick={() => switchMode(mode === "handsfree" ? "ptt" : "handsfree")} variant="ghost">
                    {mode === "handsfree" ? <><Hand size={14} /> Switch to push-to-talk</> : <><Radio size={14} /> Switch to hands-free</>}
                  </Btn>
                  <Btn data-testid="talk-stop-button" onClick={stop} variant="ghost">
                    <Square size={14} /> End Conversation
                  </Btn>
                </div>
              </>
            )}
          </div>

          <div className="mt-6 w-full">
            <label className="mb-1.5 block text-left text-xs font-semibold text-neutral-600">Agent</label>
            <select
              data-testid="talk-agent-select"
              className={inputCls}
              value={agentId}
              onChange={(e) => { setAgentId(e.target.value); sessionRef.current = null; }}
            >
              <option value="">Savy Kerno assistant (default)</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.agent_name}</option>)}
            </select>
          </div>

          {errorMsg && (
            <p data-testid="talk-error" className="mt-4 flex items-center gap-2 rounded-xl bg-red-50 px-4 py-2.5 text-left text-xs text-red-600">
              <AlertTriangle size={14} className="shrink-0" /> {errorMsg}
            </p>
          )}
          {!recorderSupported && (
            <div className="mt-4 w-full">
              <Notice
                testid="talk-mic-unsupported"
                title="Voice input not supported in this browser"
                body="Microphone recording needs a modern browser such as Chrome, Edge, Firefox or Safari. You can still type below, and spoken replies will play where audio is available."
              />
            </div>
          )}
        </Card>

        <Card className="flex min-h-[420px] min-w-0 flex-col p-5" data-testid="talk-transcript">
          <p className="mb-3 text-xs font-bold uppercase tracking-wider text-neutral-500">Conversation</p>
          <div className="flex-1 space-y-3 overflow-y-auto rounded-xl bg-neutral-50/70 p-4">
            {messages.length === 0 && (
              <p className="py-10 text-center text-xs text-neutral-400">
                Press “Start Conversation” and speak — or type a message below.
              </p>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role !== "user" && (
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#111111] text-white">
                    <Bot size={13} />
                  </span>
                )}
                <p
                  data-testid={`transcript-msg-${i}`}
                  className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#111111] text-white"
                      : m.role === "system"
                        ? "border border-dashed border-neutral-300 bg-white text-neutral-500 italic"
                        : "border border-neutral-200 bg-white text-[#111111]"
                  }`}
                >
                  {m.text}
                </p>
                {m.role === "user" && (
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-neutral-200 text-neutral-600">
                    <UserIcon size={13} />
                  </span>
                )}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <form
            className="mt-3 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              turnTimingRef.current = { t0: performance.now() };
              sendMessage(text);
              setText("");
            }}
          >
            <input
              data-testid="talk-text-input"
              className={inputCls}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type a message…"
            />
            <Btn data-testid="talk-send-button" type="submit" disabled={!text.trim() || status === "processing"}>
              <Send size={15} />
            </Btn>
          </form>
        </Card>
      </div>
      )}
    </div>
  );
}
